import React from 'react';

interface DonutSlice {
  label: string;
  value: number;
}

interface DonutChartProps {
  data: DonutSlice[];
  colors?: string[];
  size?: number;
  centerLabel?: string;
  centerSub?: string;
  formatValue?: (v: number) => string;
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function DonutChart({
  data,
  colors = DEFAULT_COLORS,
  size = 200,
  centerLabel,
  centerSub,
  formatValue,
}: DonutChartProps) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return <div className="empty-state" style={{ padding: 40 }}>Keine Daten vorhanden</div>;
  }

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 18;
  const strokeWidth = 24;
  const circumference = 2 * Math.PI * r;

  // Small gap between segments (only if >1 segment)
  const segGap = data.length > 1 ? 3 : 0;

  let offset = 0;

  const displayLabel = centerLabel ?? String(total);
  const displaySub = centerSub ?? 'Gesamt';
  const fmtVal = formatValue ?? ((v: number) => String(v));

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} opacity={0.5} />
        {data.map((slice, i) => {
          const pct = slice.value / total;
          const dashLen = Math.max(0, pct * circumference - segGap);
          const dashGap = circumference - dashLen;
          const currentOffset = offset;
          offset += pct * circumference;
          return (
            <circle
              key={slice.label}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={colors[i % colors.length]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLen} ${dashGap}`}
              strokeDashoffset={-currentOffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{ transition: 'stroke-dasharray 0.4s ease, stroke-dashoffset 0.4s ease' }}
            />
          );
        })}
        {/* Center text */}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--text)" fontFamily="Georgia, serif">
          {displayLabel}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10.5" fill="var(--text-muted)">
          {displaySub}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 140 }}>
        {data.map((slice, i) => {
          const pct = ((slice.value / total) * 100).toFixed(1);
          return (
            <div key={slice.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem' }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: colors[i % colors.length], flexShrink: 0,
                boxShadow: `0 0 0 2px ${colors[i % colors.length]}33`,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--text)', fontWeight: 500, lineHeight: 1.2 }}>{slice.label}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
                  {fmtVal(slice.value)} · {pct} %
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
